import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';

interface CampaignAnalytics {
  campaign: {
    id: number;
    name: string;
    slug: string;
    description: string | null;
    target_url: string | null;
    is_active: number;
  };
  summary: {
    visitors: number;
    unique_visitors: number;
    page_views: number;
    top_country: string | null;
  };
  daily_trend: Array<{ date: string; visitors: number; page_views: number }>;
  device_breakdown: Array<{ device: string; count: number }>;
  top_pages: Array<{ page_url: string; views: number; unique_visitors: number }>;
  countries: Array<{ country: string; count: number }>;
}

const countryFlag = (code: string) => {
  if (!code || code.length !== 2) return '🌐';
  const upper = code.toUpperCase();
  return String.fromCodePoint(0x1F1E6 + upper.charCodeAt(0) - 65) +
    String.fromCodePoint(0x1F1E6 + upper.charCodeAt(1) - 65);
};

const CampaignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<CampaignAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [copied, setCopied] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  useDocumentTitle(data?.campaign?.name ? `Campaign: ${data.campaign.name}` : 'Campaign Analytics');

  const fetchAnalytics = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await campaignAPI.getAnalytics(Number(id), days);
      setData(response.data.data || response.data);
    } catch (error: any) {
      toast.error('Failed to load campaign analytics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id, days]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const getCampaignUrl = () => {
    if (!data) return '';
    const base = data.campaign.target_url || 'https://yoursite.com';
    return `${base}?cmp=${data.campaign.slug}`;
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(getCampaignUrl()).then(() => {
      setCopied(true);
      toast.success('URL copied');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleToggleStatus = async () => {
    if (!data) return;
    setToggleLoading(true);
    try {
      await campaignAPI.update(data.campaign.id, {
        is_active: data.campaign.is_active ? 0 : 1,
      });
      toast.success(`Campaign ${data.campaign.is_active ? 'deactivated' : 'activated'}`);
      fetchAnalytics();
    } catch {
      toast.error('Failed to update campaign');
    } finally {
      setToggleLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!data) return;
    if (!window.confirm(`Delete campaign "${data.campaign.name}"? This cannot be undone.`)) return;
    try {
      await campaignAPI.delete(data.campaign.id);
      toast.success('Campaign deleted');
      navigate('/analytics/campaigns');
    } catch {
      toast.error('Failed to delete campaign');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="text-center py-5">
          <i className="bi bi-exclamation-circle fs-1 text-muted d-block mb-3"></i>
          <h5>Campaign not found</h5>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/analytics/campaigns')}>
            Back to Campaigns
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Back link */}
        <div className="mb-3">
          <button
            className="btn btn-link p-0 text-muted text-decoration-none"
            onClick={() => navigate('/analytics/campaigns')}
          >
            <i className="bi bi-arrow-left me-1"></i>Back to Campaigns
          </button>
        </div>

        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h4 className="fw-bold mb-0">{data.campaign.name}</h4>
              <span className={`badge ${data.campaign.is_active ? 'bg-success' : 'bg-secondary'}`}>
                {data.campaign.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted font-monospace text-break">{getCampaignUrl()}</small>
              <button
                className="btn btn-sm btn-link p-0 text-muted"
                onClick={copyUrl}
                title="Copy URL"
              >
                <i className={`bi ${copied ? 'bi-check-lg text-success' : 'bi-clipboard'}`}></i>
              </button>
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            {/* Period Selector */}
            <div className="btn-group">
              {[7, 30, 90].map(d => (
                <button
                  key={d}
                  className={`btn btn-sm ${days === d ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setDays(d)}
                >
                  {d}d
                </button>
              ))}
            </div>
            <button
              className={`btn btn-sm ${data.campaign.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
              onClick={handleToggleStatus}
              disabled={toggleLoading}
            >
              <i className={`bi ${data.campaign.is_active ? 'bi-pause' : 'bi-play'} me-1`}></i>
              {data.campaign.is_active ? 'Deactivate' : 'Activate'}
            </button>
            <button className="btn btn-sm btn-outline-danger" onClick={handleDelete}>
              <i className="bi bi-trash me-1"></i>Delete
            </button>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-2 bg-primary bg-opacity-10 text-primary rounded me-3">
                    <i className="bi bi-people fs-4"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-0">Visitors</p>
                    <h4 className="mb-0 fw-bold">{(data.summary?.visitors ?? 0).toLocaleString()}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-2 bg-success bg-opacity-10 text-success rounded me-3">
                    <i className="bi bi-person-check fs-4"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-0">Unique Visitors</p>
                    <h4 className="mb-0 fw-bold">{(data.summary?.unique_visitors ?? 0).toLocaleString()}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-2 bg-info bg-opacity-10 text-info rounded me-3">
                    <i className="bi bi-eye fs-4"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-0">Page Views</p>
                    <h4 className="mb-0 fw-bold">{(data.summary?.page_views ?? 0).toLocaleString()}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-2 bg-warning bg-opacity-10 text-warning rounded me-3">
                    <i className="bi bi-globe fs-4"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-0">Top Country</p>
                    <h4 className="mb-0 fw-bold" style={{ fontSize: '16px' }}>
                      {data.summary?.top_country || '—'}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="row g-4 mb-4">
          {/* Daily Trend */}
          <div className="col-12 col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h6 className="mb-0">Daily Trend</h6>
              </div>
              <div className="card-body">
                {data.daily_trend && data.daily_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={data.daily_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11 }}
                        tickFormatter={v => {
                          const d = new Date(v);
                          return `${d.getMonth() + 1}/${d.getDate()}`;
                        }}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip
                        labelFormatter={v => new Date(v).toLocaleDateString()}
                      />
                      <Line
                        type="monotone"
                        dataKey="visitors"
                        stroke="#3B82F6"
                        strokeWidth={2}
                        dot={false}
                        name="Visitors"
                      />
                      <Line
                        type="monotone"
                        dataKey="page_views"
                        stroke="#10B981"
                        strokeWidth={2}
                        dot={false}
                        name="Page Views"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center text-muted py-5">No trend data available</div>
                )}
              </div>
            </div>
          </div>

          {/* Device Breakdown */}
          <div className="col-12 col-lg-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h6 className="mb-0">Device Breakdown</h6>
              </div>
              <div className="card-body">
                {data.device_breakdown && data.device_breakdown.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.device_breakdown} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="device" type="category" tick={{ fontSize: 11 }} width={70} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#3B82F6" name="Visitors" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center text-muted py-5">No device data available</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tables Row */}
        <div className="row g-4">
          {/* Top Pages */}
          <div className="col-12 col-lg-7">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h6 className="mb-0">Top Pages</h6>
              </div>
              <div className="card-body p-0">
                {data.top_pages && data.top_pages.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Page URL</th>
                          <th className="text-end">Views</th>
                          <th className="text-end">Unique Visitors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top_pages.map((page, index) => (
                          <tr key={index}>
                            <td>
                              <small className="font-monospace text-truncate d-block" style={{ maxWidth: '280px' }}>
                                {page.page_url}
                              </small>
                            </td>
                            <td className="text-end">{page.views}</td>
                            <td className="text-end">{page.unique_visitors}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">No page data available</div>
                )}
              </div>
            </div>
          </div>

          {/* Countries */}
          <div className="col-12 col-lg-5">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h6 className="mb-0">Countries</h6>
              </div>
              <div className="card-body p-0">
                {data.countries && data.countries.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Country</th>
                          <th className="text-end">Visitors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.countries.map((country, index) => (
                          <tr key={index}>
                            <td>
                              <span className="me-2">{countryFlag(country.country)}</span>
                              {country.country || 'Unknown'}
                            </td>
                            <td className="text-end">{country.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">No country data available</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CampaignDetailPage;
